import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import IconButton from '@/components/IconButton/IconButton';
import { cn } from '@/lib/cn';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /**
   * Whether this modal pushes/pops a history entry to support browser-back
   * gesture. Set to `false` for nested modals so closing the inner one
   * doesn't pop the outer modal's entry.
   */
  manageHistory?: boolean;
}

const sizeStyles: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
};

export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
  manageHistory = true,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const ownsHistoryEntry = useRef(false);

  // History-API back-gesture: push a state when opening, listen for popstate.
  useEffect(() => {
    if (!open) return;
    previousFocus.current = (document.activeElement as HTMLElement) ?? null;
    if (!manageHistory) return;

    // Only push if we don't already own an entry (avoids double-push in StrictMode).
    if (!ownsHistoryEntry.current) {
      window.history.pushState({ modal: true }, '');
      ownsHistoryEntry.current = true;
    }

    const handlePop = () => {
      ownsHistoryEntry.current = false;
      onCloseRef.current();
    };
    window.addEventListener('popstate', handlePop);

    return () => {
      window.removeEventListener('popstate', handlePop);
    };
  }, [open, manageHistory]);

  // Pop history entry when modal closes by means other than back-gesture.
  useEffect(() => {
    if (open) return;
    if (ownsHistoryEntry.current) {
      ownsHistoryEntry.current = false;
      window.history.back();
    }
    previousFocus.current?.focus?.();
  }, [open]);

  // Focus first focusable element when opened.
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = dialog.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
    );
    (focusable ?? dialog).focus();
  }, [open]);

  if (!open) return null;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Stop all key events from bubbling through the React tree to ancestors.
    // Portals bubble events through React parents, so without this an Enter/Space
    // on a control inside the modal can re-trigger handlers on the component that
    // rendered the modal (e.g. a clickable Widget body).
    e.stopPropagation();
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'Tab') {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'),
      ).filter((el) => !el.hasAttribute('inert'));
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 motion-safe:animate-[fadein_120ms_ease-out]"
      style={{ background: 'var(--bg-overlay)' }}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative w-full overflow-hidden rounded-xl border border-(--border-default) bg-(--bg-surface-strong) text-(--text-primary) shadow-(--shadow-modal) backdrop-blur-xl',
          'motion-safe:animate-[modalIn_180ms_cubic-bezier(0.16,1,0.3,1)]',
          sizeStyles[size],
          className,
        )}
      >
        {(title || description) && (
          <header className="flex items-start justify-between gap-4 border-b border-(--border-subtle) px-6 py-5">
            <div className="min-w-0 flex-1">
              {title && <h2 className="font-display text-xl font-semibold text-(--text-primary)">{title}</h2>}
              {description && <p className="mt-1 text-sm text-(--text-secondary)">{description}</p>}
            </div>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="Close"
              onClick={onClose}
              icon={
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-4 w-4">
                  <path d="M5.3 4.3a1 1 0 0 1 1.4 0L10 7.6l3.3-3.3a1 1 0 1 1 1.4 1.4L11.4 9l3.3 3.3a1 1 0 0 1-1.4 1.4L10 10.4l-3.3 3.3a1 1 0 0 1-1.4-1.4L8.6 9 5.3 5.7a1 1 0 0 1 0-1.4z" />
                </svg>
              }
            />
          </header>
        )}
        <div className="max-h-[calc(100dvh-12rem)] overflow-y-auto px-6 py-5">{children}</div>
        {footer && <footer className="border-t border-(--border-subtle) px-6 py-4">{footer}</footer>}
      </div>

      <style>{`
        @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>,
    document.body,
  );
}
